import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {

    ArrowRight,

} from 'lucide-react';
import { motion } from 'framer-motion';

export interface ServiceCardProps {
    icon: React.ReactNode;
    title: string;
    description: string;
    buttonText: string;
    buttonLink: string;
    image?: string;
    gradient?: string;
    isComingSoon?: boolean;
    stats?: {
        label: string;
        value: string;
    }[];
    badge?: string;
    isCommingSoon?: boolean;
}

const ServiceCard: React.FC<ServiceCardProps> = ({
    icon,
    title,
    description,
    buttonText,
    buttonLink,
    image,
    gradient = "from-green-500 to-emerald-600",
    isComingSoon = false,
    stats,
    badge
}) => {
    const navigate = useNavigate();
    const { t } = useTranslation();

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="group relative bg-white rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-500 hover:-translate-y-1.5 flex flex-col justify-between"
        >
            {isComingSoon && <div className='absolute inset-0 bg-black/40 z-10' />}
            {/* Badge */}
            {badge && (
                <div className="absolute top-3 right-3 z-20">
                    <span className="px-2.5 py-0.5 text-[11px] font-semibold text-white bg-gradient-to-r from-yellow-400 to-orange-500 rounded-full shadow-md">
                        {badge}
                    </span>
                </div>
            )}

            {/* Coming Soon Badge */}
            {isComingSoon && (
                <div className="absolute top-3 right-3 z-20">
                    <span className="px-2.5 py-0.5 text-[11px] font-semibold text-white bg-gradient-to-r from-purple-500 to-pink-500 rounded-full shadow-md animate-pulse">
                        🚀 {t("bientot") || "Bientôt"}
                    </span>
                </div>
            )}

            {/* Image Section */}
            {image && (
                <div className="relative h-28 sm:h-36 overflow-hidden">
                    <img
                        src={image}
                        alt={title}
                        loading="lazy"
                        decoding="async"
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />

                    {/* Icon overlay on image */}
                    <div className="absolute bottom-3 left-3 z-20">
                        <div className={`p-2.5 bg-gradient-to-br ${gradient} rounded-xl shadow-md`}>
                            {icon}
                        </div>
                    </div>
                </div>
            )}

            {/* Content */}
            <div className={`p-4 sm:p-5 flex flex-col flex-1 justify-between ${!image ? 'pt-6' : ''}`}>
                <div>
                    {/* Icon if no image */}
                    {!image && (
                        <div className={`inline-flex p-3 bg-gradient-to-br ${gradient} rounded-xl shadow-md mb-3`}>
                            {icon}
                        </div>
                    )}

                    <h3 className="text-base sm:text-lg font-bold text-gray-900 mb-1.5 group-hover:text-green-600 transition-colors duration-300">
                        {title}
                    </h3>

                    <p className="text-gray-600 text-xs sm:text-sm leading-relaxed mb-3 line-clamp-2">
                        {description}
                    </p>

                    {/* Stats */}
                    {stats && (
                        <div className="grid grid-cols-2 gap-2 mb-3 p-2 bg-gray-50 rounded-lg">
                            {stats.map((stat, index) => (
                                <div key={index} className="text-center">
                                    <p className="text-xs sm:text-sm font-bold text-green-600">{stat.value}</p>
                                    <p className="text-[10px] text-gray-500">{stat.label}</p>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Button */}
                <button
                    onClick={() => navigate(buttonLink)}
                    className={`w-full py-2 px-3 bg-gradient-to-r ${gradient} text-white rounded-xl text-xs sm:text-sm font-medium transition-all duration-300 hover:shadow-md hover:shadow-green-500/30 flex items-center justify-center gap-1.5 group/btn mt-2`}
                >
                    <span>{buttonText}</span>
                    <ArrowRight className="w-3.5 h-3.5 transition-transform duration-300 group-hover/btn:translate-x-1" />
                </button>
            </div>
        </motion.div>
    );
};

export default ServiceCard